import { useI18n } from "../i18n/I18nProvider";
import { localizeError } from "../auth/localizeError";
import { useEffect, useState } from "react";
import type { Api } from "../types";
export type Consent = {
  kind: "history" | "training";
  granted: boolean;
  version: string;
};
export function Consents({
  api,
  onHistoryChange,
}: {
  api: Api;
  onHistoryChange: (value: boolean) => void;
}) {
  const { t, locale } = useI18n();
  const [etag, setEtag] = useState<string | undefined>();
  const [items, setItems] = useState<Consent[]>([]),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  useEffect(() => {
    let alive = true;
    api
      .request<{ items: Consent[]; etag?: string }>("/consents")
      .then((result) => {
        if (alive) { setItems(result.items); setEtag(result.etag); }
      })
      .catch((e) => {
        if (alive) setError(localizeError(e, locale));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [api]);
  async function change(kind: Consent["kind"], granted: boolean) {
    const previous = items;
    setItems((old) => [
      ...old.filter((item) => item.kind !== kind),
      { kind, granted, version: "v1" },
    ]);
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api.request("/consents", {
        method: "PATCH",
        body: { kind, granted, ...(etag ? { expected_etag: etag } : {}) },
      });
      const result = await api.request<{ items: Consent[]; etag?: string }>("/consents");
      setItems(result.items);
      setEtag(result.etag);
      if (kind === "history")
        onHistoryChange(
          result.items.some((item) => item.kind === "history" && item.granted),
        );
      setNotice(t("授权偏好已保存", "Data choices saved."));
    } catch (e) {
      setItems(previous);
      setError(localizeError(e, locale));
      if ((e as {status?:number}).status === 409) {
        try {
          const current = await api.request<{items: Consent[]; etag?: string}>("/consents");
          setItems(current.items); setEtag(current.etag);
          onHistoryChange(current.items.some(item => item.kind === "history" && item.granted));
        } catch { /* Keep the last confirmed choices and allow a page reload. */ }
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="settings-card">
      <h2>{t("数据授权与偏好", "Data choices")}</h2>
      {error && (
        <p className="notice error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      <label className="consent-check">
        <input
          type="checkbox"
          checked={items.some((i) => i.kind === "history" && i.granted)}
          disabled={loading || busy}
          onChange={(e) => void change("history", e.target.checked)}
        />
        {t("保存对话历史（使用聊天所需）", "Save conversation history (required for chatting)")}
      </label>
      <p className="muted">
        {t("聊天与持续陪伴需要保存对话记录，以便刷新后恢复和继续会谈。撤回后无法新建对话；已有记录可单独删除或随账户注销清理。", "Chat and ongoing courses save conversation history so you can return and continue. If you withdraw permission, you cannot start new conversations. Existing records can be deleted individually or with your account.")}
      </p>
      <label className="consent-check">
        <input
          type="checkbox"
          checked={items.some((i) => i.kind === "training" && i.granted)}
          disabled={loading || busy}
          onChange={(e) => void change("training", e.target.checked)}
        />
        {t("允许将对话用于训练（偏好记录）", "Allow conversations to be used for training (preference only)")}
      </label>
      <p className="muted">
        {t("此选项默认关闭。目前仅保存你的选择，尚未启用将对话用于训练的流程。", "Off by default. We currently only save your choice; conversation training is not enabled.")}
      </p>
    </section>
  );
}
